/**
 * Finance report — on-screen preview: KPI tiles, recharts pies/bars, tables.
 * Renders the exact model that is sent to the PDF callable, so preview ≈ PDF.
 */

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { formatCurrency } from "./financeReportModel";

const tooltipStyle = {
  background: "#fff",
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 6,
  fontSize: 12,
  direction: "rtl",
};

const pct = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

function PieCard({ chart }) {
  const total = chart.data.reduce((s, d) => s + d.value, 0);
  if (chart.data.length === 0) {
    return (
      <div className="frx-chart-card">
        <h4>{chart.title}</h4>
        <div className="frx-chart-empty">אין נתונים</div>
      </div>
    );
  }
  return (
    <div className="frx-chart-card">
      <h4>{chart.title}</h4>
      <div className="frx-chart-body">
        <div className="frx-chart-pie">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={chart.data}
                dataKey="value"
                nameKey="label"
                innerRadius={48}
                outerRadius={88}
                paddingAngle={2}
                stroke="none"
              >
                {chart.data.map((d) => (
                  <Cell key={d.label} fill={d.color} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="frx-legend">
          {chart.data.map((d) => (
            <li key={d.label}>
              <span className="frx-legend-dot" style={{ background: d.color }} />
              <span className="frx-legend-name">{d.label}</span>
              <span className="frx-legend-val">{formatCurrency(d.value)}</span>
              <span className="frx-legend-pct">{pct(d.value, total)}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BarCard({ chart }) {
  if (chart.data.length === 0) {
    return (
      <div className="frx-chart-card frx-chart-card--wide">
        <h4>{chart.title}</h4>
        <div className="frx-chart-empty">אין נתונים</div>
      </div>
    );
  }
  const grouped = chart.type === "groupedBar";
  return (
    <div className="frx-chart-card frx-chart-card--wide">
      <h4>{chart.title}</h4>
      <div className="frx-chart-bars">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chart.data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₪${v.toLocaleString("he-IL")}`} width={70} />
            <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={tooltipStyle} />
            {grouped ? (
              <>
                <Legend />
                {chart.series.map((s) => (
                  <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} />
                ))}
              </>
            ) : (
              <Bar dataKey="value" name="נגבה" radius={[4, 4, 0, 0]}>
                {chart.data.map((d) => (
                  <Cell key={d.label} fill={d.color} />
                ))}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TableCard({ table }) {
  return (
    <div className="frx-table-card">
      <h4>
        {table.title}
        <span className="frx-table-count">{table.rows.length}</span>
      </h4>
      {table.rows.length === 0 ? (
        <div className="frx-chart-empty">אין נתונים</div>
      ) : (
        <div className="prw-table-wrap">
          <table className="prw-table">
            <thead>
              <tr>
                {table.columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={i}>
                  {table.columns.map((c) =>
                    c.key === "status" && row.statusColor ? (
                      <td key={c.key}>
                        <span className="frx-status" style={{ "--st": row.statusColor }}>
                          {row[c.key]}
                        </span>
                      </td>
                    ) : (
                      <td key={c.key}>{row[c.key]}</td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FinancePreview({ model }) {
  const { kpis, charts, tables } = model;
  return (
    <div className="frx-preview">
      {kpis.length > 0 && (
        <div className="frx-kpis">
          {kpis.map((k) => (
            <div key={k.key} className="frx-kpi">
              <span className="frx-kpi-label">{k.label}</span>
              <span className="frx-kpi-value">{k.value}</span>
            </div>
          ))}
        </div>
      )}

      {charts.length > 0 && (
        <div className="frx-charts">
          {charts.map((c) =>
            c.type === "pie" ? <PieCard key={c.key} chart={c} /> : <BarCard key={c.key} chart={c} />
          )}
        </div>
      )}

      {tables.map((t) => (
        <TableCard key={t.key} table={t} />
      ))}
    </div>
  );
}

export default FinancePreview;
